// One AudioContext for the page's mic and its speaker. iOS Safari routes all of a page's audio
// through one audio session: a second context (the player's next to the mic's) fights it for the
// route, and when the mic opens (Bluetooth HFP at 16 or 24 kHz) one of them is often left
// "running" with a clock that never moves (phone diagnostics: playerCtx "running", playerTime 0,
// while the mic gave nothing). So the capture and the player share this hub's context, and when
// it has to be replaced (the route changed, its clock froze, the mic went deaf) both move to the
// new one together: the hub tells its users (onSwap) before it closes the old one.
//
// iOS starts a context only from a user gesture, or while the page has a live mic track (WebKit
// lifts its autoplay rule for a capturing page). So: prepare() inside the tap creates or resumes
// it, and a rebuild after getUserMedia resolved (the track is live) may run without one.

export interface AudioHubOptions {
  latencyHint?: AudioContextLatencyCategory;
  /** Tests: how a context is made. */
  create?: () => AudioContext;
  /** Tests: the clock. */
  now?: () => number;
}

/** Why the context was replaced; logged with the rebuild. */
export type SwapListener = (next: AudioContext, prev: AudioContext | null, reason: string) => void;

/** A "running" context whose clock hasn't moved for this long is frozen (dead on iOS). */
export const FROZEN_MS = 1200;

export class AudioHub {
  private current: AudioContext | null = null;
  private readonly swaps = new Set<SwapListener>();
  private readonly stateFns = new Set<(ctx: AudioContext) => void>();
  /** The page was hidden or a call interrupted the context: the next tap may need a fresh one. */
  private interruptedSeen = false;
  /** Set ahead (pre-warm): the next prepare() builds a fresh context inside the tap. */
  private freshNext: string | null = null;
  private clock = { time: -1, at: 0 };
  private builtAt = -Infinity;
  /** Times the context was (re)built, and why the last time. */
  builds = 0;
  lastReason = "";

  constructor(private readonly opts: AudioHubOptions = {}) {}

  private now(): number {
    return (this.opts.now ?? (() => performance.now()))();
  }

  /** The context, if one was made (it may not be running). */
  get ctx(): AudioContext | null {
    return this.current;
  }

  /** The context, made now if there is none (outside a gesture it starts suspended). */
  context(): AudioContext {
    return this.current && this.current.state !== "closed" ? this.current : this.rebuild(this.current ? "closed" : "first");
  }

  /**
   * Call synchronously inside the user's tap: a fresh context if there is none, it closed, a call
   * left it interrupted, or a fresh one was asked for ahead (`freshOnNextTap`); then resume().
   */
  prepare(): AudioContext {
    const ctx = this.current;
    let reason: string | null = null;
    if (!ctx) reason = "first";
    else if (ctx.state === "closed") reason = "closed";
    else if ((ctx.state as string) === "interrupted" || this.interruptedSeen) reason = "after_interruption";
    else if (this.freshNext) reason = this.freshNext;
    const use = reason ? this.rebuild(reason) : ctx!;
    if (use.state !== "running") void use.resume().catch(() => {});
    return use;
  }

  /** How long ago the context was last (re)built. */
  msSinceBuild(): number {
    return this.now() - this.builtAt;
  }

  /** Pre-warm: the next prepare() (in a tap) builds a fresh context rather than trying this one. */
  freshOnNextTap(reason: string): void {
    this.freshNext = reason;
  }

  /**
   * Replace the context: the users move to the new one (onSwap, before the old one closes), then
   * the old one is closed and the new one asked to run.
   */
  rebuild(reason: string): AudioContext {
    const prev = this.current;
    const next = this.opts.create ? this.opts.create() : new AudioContext({ latencyHint: this.opts.latencyHint ?? "interactive" });
    this.current = next;
    this.builds++;
    this.builtAt = this.now();
    this.lastReason = reason;
    this.freshNext = null;
    this.interruptedSeen = false;
    this.clock = { time: -1, at: 0 };
    next.onstatechange = () => {
      if (next !== this.current) return;
      if ((next.state as string) === "interrupted") this.interruptedSeen = true;
      this.stateFns.forEach((fn) => fn(next));
    };
    this.swaps.forEach((fn) => fn(next, prev, reason));
    if (prev && prev.state !== "closed") void prev.close().catch(() => {});
    if (next.state !== "running") void next.resume().catch(() => {});
    return next;
  }

  /** The context changed (a rebuild): move your nodes to `next`. Returns an unsubscribe. */
  onSwap(fn: SwapListener): () => void {
    this.swaps.add(fn);
    return () => this.swaps.delete(fn);
  }

  /** The context's state changed (suspended, interrupted, running). */
  onState(fn: (ctx: AudioContext) => void): () => void {
    this.stateFns.add(fn);
    return () => this.stateFns.delete(fn);
  }

  /** A call took the audio (iOS "interrupted"): a later tap builds afresh. */
  interrupted(): boolean {
    return (this.current?.state as string | undefined) === "interrupted";
  }

  /** The audio came back by itself: the context in use is fine again. */
  clearInterrupted(): void {
    this.interruptedSeen = false;
  }

  /**
   * True when the context says "running" but its clock hasn't moved for FROZEN_MS (iOS leaves a
   * context like that when the route changed under it). Sampled on each call: call it regularly.
   */
  frozen(): boolean {
    const ctx = this.current;
    if (!ctx || ctx.state !== "running") {
      this.clock = { time: -1, at: 0 };
      return false;
    }
    const now = this.now();
    if (ctx.currentTime !== this.clock.time) {
      this.clock = { time: ctx.currentTime, at: now };
      return false;
    }
    return now - this.clock.at >= FROZEN_MS;
  }

  stats(): Record<string, unknown> {
    const ctx = this.current;
    return {
      hubCtx: ctx?.state ?? "none",
      hubRate: ctx?.sampleRate ?? 0,
      hubTime: ctx ? Math.round(ctx.currentTime * 10) / 10 : 0,
      hubBuilds: this.builds,
      hubReason: this.lastReason,
    };
  }

  close(): void {
    const ctx = this.current;
    this.current = null;
    if (ctx && ctx.state !== "closed") void ctx.close().catch(() => {});
  }
}
